import React from 'react';
import { Sparkles, FileText, AlertTriangle, BookOpen } from 'lucide-react';
import { fmt$ } from './constants';
import { CitationLink } from './Citations';
import SimpleMarkdown from '../../components/SimpleMarkdown';

export function CalcSummaryTab({ data }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-widest font-bold mb-2 text-brand-accent">Breakdown</div>
        {(data.breakdown || []).length === 0 && <div className="text-xs text-slate-500 italic">No breakdown captured.</div>}
        <div className="space-y-1.5">
          {(data.breakdown || []).map((b, i) => (
            <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-50 last:border-b-0">
              <span className="text-slate-600">{b.label}</span>
              <span className="font-semibold text-brand-navy">
                {typeof b.value === 'number' ? fmt$(b.value) : (b.value || '—')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {data.variable_income?.detected && (
        <div className="rounded-lg p-3 border" style={{ background: '#FFF7ED', borderColor: '#f59e0b' }}>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-bold mb-1" style={{ color: '#92400e' }}>
            <AlertTriangle size={11} /> Variable Income Rule Triggered
          </div>
          <div className="text-xs text-slate-700">
            Types: <b>{(data.variable_income.types || []).join(', ')}</b>
          </div>
          <div className="text-[11px] text-slate-600 mt-1">{data.variable_income.guideline}</div>
        </div>
      )}

      {data.employer && (
        <div className="text-xs text-slate-500"><b>Employer:</b> {data.employer}</div>
      )}
    </div>
  );
}

export function CalcDocsTab({ docs, aggregate }) {
  if (!docs || docs.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 p-4 text-center bg-slate-50">
        <FileText size={22} className="mx-auto text-slate-400 mb-2" />
        <div className="text-sm font-semibold text-slate-700">Manual entry — no source documents</div>
        <div className="text-xs text-slate-500 mt-1">
          This calculation was created via typed inputs. Drop paystubs/W-2s/tax returns in the
          drop zone above to auto-derive figures and capture a document audit trail.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-[11px] uppercase tracking-widest font-bold text-brand-accent">
        Source Documents ({docs.length})
      </div>
      {docs.map((d, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: '#F3FAE8' }}>
              <FileText size={14} style={{ color: '#3a5717' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-brand-navy">{d.filename}</div>
              <div className="text-[11px] text-slate-500">{d.classified_label || d.classified_as}</div>
            </div>
          </div>
          {d.key_figures && Object.keys(d.key_figures).length > 0 && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 pt-2 border-t border-slate-100">
              {Object.entries(d.key_figures).map(([k, v]) => (
                <div key={k} className="flex justify-between text-[12px]">
                  <span className="text-slate-500">{k.replace(/_/g, ' ')}</span>
                  <span className="font-semibold text-slate-700">
                    {typeof v === 'number' ? fmt$(v) : String(v)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {aggregate && aggregate.total_ytd > 0 && (
        <div className="rounded-lg p-3 border mt-4" style={{ background: '#F8FAFC', borderColor: '#e2e8f0' }}>
          <div className="text-[11px] uppercase tracking-wide font-bold text-slate-600 mb-2">Aggregate Roll-Up</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] uppercase text-slate-500">Base YTD</div>
              <div className="font-serif font-bold text-sm" style={{ color: '#3a5717' }}>{fmt$(aggregate.base_ytd)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500">Variable YTD</div>
              <div className="font-serif font-bold text-sm" style={{ color: aggregate.variable_ytd > 0 ? '#92400e' : '#64748b' }}>{fmt$(aggregate.variable_ytd)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500">Total YTD</div>
              <div className="font-serif font-bold text-sm text-brand-navy">{fmt$(aggregate.total_ytd)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CalcReasoningTab({ data, citations }) {
  return (
    <div className="space-y-4">
      {/* AI explanation */}
      <div>
        <div className="text-[11px] uppercase tracking-widest font-bold mb-2 flex items-center gap-1 text-brand-accent">
          <Sparkles size={11} /> Audrey's Reasoning
        </div>
        {data.ai_explanation ? (
          <div className="rounded-lg p-3 text-sm leading-relaxed"
               style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(79,70,229,0.06) 100%)', border: '1px solid rgba(124,58,237,0.15)' }}>
            <SimpleMarkdown text={data.ai_explanation} />
          </div>
        ) : (
          <div className="text-xs text-slate-500 italic">No AI explanation recorded for this calculation.</div>
        )}
      </div>

      {/* Method note (rule summary) */}
      <div>
        <div className="text-[11px] uppercase tracking-widest font-bold mb-1 text-brand-accent">Calculation Method</div>
        <div className="text-sm text-slate-700 rounded-lg p-3 bg-slate-50 border border-slate-200">{data.method}</div>
      </div>

      {/* Citations */}
      <div>
        <div className="text-[11px] uppercase tracking-widest font-bold mb-2 flex items-center gap-1 text-brand-accent">
          <BookOpen size={11} /> Applicable Guidelines
        </div>
        {(!citations || citations.length === 0) && (
          <div className="text-xs text-slate-500 italic">No guideline citations recorded for this calculation.</div>
        )}
        <div className="flex flex-wrap gap-2">
          {(citations || []).map((c, i) => (
            <CitationLink
              key={i}
              topic={c.topic}
              agency={c.agency || data.agency_guideline}
              label={c.label || c.topic}
            />
          ))}
        </div>
        {data.agency_guideline && (
          <div className="text-[11px] text-slate-500 mt-3">
            Program applied: <b className="uppercase text-brand-navy">{data.agency_guideline}</b>
          </div>
        )}
      </div>
    </div>
  );
}

export function CalcRawTab({ data }) {
  const cleanInput = Object.fromEntries(
    Object.entries(data.input || {}).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-bold mb-2 text-brand-accent">Raw Inputs (audit trail)</div>
      <pre className="text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto">
{JSON.stringify(cleanInput, null, 2)}
      </pre>
    </div>
  );
}
